import {
  ChangeDetectionStrategy,
  Component,
  InputSignal,
  Signal,
  TemplateRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { LetDirective } from '@ngrx/component';
import { Observable } from 'rxjs';

import { CoreInsightsEvent, InsightsEventsTrackingService } from '@core/shared/util';
import {
  CreateTicketButtonComponent,
  HelpCenterDirectLinkButtonComponent,
  ZendeskHelpCenterButtonComponent,
} from '@mp/shared/helper-links/help-center/ui';
import { ZendeskSupportDialogContainerComponent } from '@mp/shared/helper-links/zendesk-support-dialog/feature';
import {
  ZENDESK_HC_ACCESS_TOKEN_KEY,
  ZendeskOAuthScope,
  provideZendeskAuthDataAccess,
} from '@mp/shared/zendesk/auth-data-access';
import { provideZendeskDataAccess } from '@mp/shared/zendesk/data-access';
import { provideZendeskApiDetailsService } from '@mp/shared/zendesk/util';

import { HelpCenterButtonViewModel } from './help-center-button-view-model';
import { HelpCenterButtonStore } from './help-center-button.store';

@Component({
  selector: 'mp-help-center-button',
  templateUrl: './help-center-button.component.html',
  styleUrl: './help-center-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LetDirective,

    HelpCenterDirectLinkButtonComponent,
    ZendeskHelpCenterButtonComponent,
    ZendeskSupportDialogContainerComponent,
    CreateTicketButtonComponent,
  ],
  providers: [
    provideZendeskApiDetailsService(),
    provideZendeskAuthDataAccess(ZENDESK_HC_ACCESS_TOKEN_KEY, ZendeskOAuthScope.ReadArticles),
    provideZendeskDataAccess(),
    HelpCenterButtonStore,
  ],
})
export class HelpCenterButtonComponent {
  private readonly store: HelpCenterButtonStore = inject(HelpCenterButtonStore);
  private readonly insightsEventsTrackingService: InsightsEventsTrackingService | null = inject(
    InsightsEventsTrackingService,
    { optional: true },
  );

  readonly isSmall: InputSignal<boolean> = input.required<boolean>();

  readonly isZendeskFeedbackActive: InputSignal<boolean> = input.required<boolean>();

  readonly createTicketButtonTemplate: Signal<TemplateRef<unknown>> =
    viewChild.required<TemplateRef<unknown>>('createTicketButtonTemplate');

  protected readonly vm$: Observable<HelpCenterButtonViewModel> = this.store.vm$;

  onOpenZendeskHelpCenterInfoFlyoutClick(): void {
    this.store.openZendeskHelpCenterInfoFlyout({
      showCreateTicketButton: this.isZendeskFeedbackActive(),
      createTicketButtonTemplate: this.createTicketButtonTemplate(),
    });
  }

  trackHelpCenterButtonClick(helpCenterButtonType: string): void {
    // NOTE: Tracking the event both for functionality and page origin
    this.insightsEventsTrackingService?.trackEvent(CoreInsightsEvent.HELP_CENTER_BUTTON_CLICK, {
      value: helpCenterButtonType,
    });

    this.insightsEventsTrackingService?.trackEvent(
      CoreInsightsEvent.HELP_CENTER_BUTTON_CLICK,
      { value: helpCenterButtonType },
      false,
    );
  }
}
